import * as React from 'react';
import {FC, useMemo, MouseEvent} from 'react';
import {useFieldArray} from "react-final-form-arrays";
import {isObject} from 'lodash';
import {ElementLogic, ElementMap} from './types';
import ListComponent from './ListComponent';
import FormElement from './FormElement';
import getRefId from './getRefId';

interface FieldSetProps {
  name: string,
  data: ElementLogic[],
  type: keyof ElementMap,
  parent: string,
  formProps?: any,
  min?: number,
  max?: number,
  index?: number,
}

const FieldSet: FC<FieldSetProps> = (props) => {
  const path = `${props.parent}`;
  // make sure the initialValue is always the same array
  const initialValue = useMemo(() => Array(props.min).fill(null).map(() => ({'id': getRefId()})), [props.min]);
  const {fields} = useFieldArray(path, {subscription: {}, validateFields: [], initialValue});

  return (
    <React.Fragment>
      {fields.map((name, index) => (
        <React.Fragment key={name}>
          <FormElement
            type={props.type}
            key={index}
            props={{
              name,
              index,
              isLastElement: fields.length - 1 === index,
              children: <ListComponent {...props} index={index} parent={name} />,
              onDelete: fields?.length > props.min
                ? () => fields.remove(index)
                : null,
              ...props?.formProps,
            }}
          />
        </React.Fragment>
      ))}
      <FormElement type="AddButton" props={{
        name: props.name,
        index: props?.index,
        item: props?.formProps?.item,
        path,
        visible: fields?.length < props.max,
        totalIndexes: fields.length,
        onClick(e: MouseEvent) {
          e.stopPropagation();
          fields.push({
            ...(props?.formProps?.newItemState || {}),
            'id': getRefId()
          });
        },
      }} />
    </React.Fragment>
  );
}

const MemoizedFieldSet = React.memo(
  FieldSet,
  (prevProps, nextProps) =>
    prevProps.name === nextProps.name
    && Array.isArray(prevProps.data) === Array.isArray(nextProps.data)
    && prevProps.type === nextProps.type
    && prevProps.parent === nextProps.parent
    && isObject(prevProps.formProps) === isObject(nextProps.formProps)
    && prevProps.min === nextProps.min
    && prevProps.max === nextProps.max
    && prevProps.index === nextProps.index
);

export default MemoizedFieldSet;
